import { useEffect, useMemo, useState } from 'react';
import { cn } from '@/lib/utils';
import { orderSubsQueryRequest, orderSubsRequest, orderUpgradeRequest } from '@/lib/service/home';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';

const cashMap = {
  JPY: 'JP¥',
  KRW: '₩',
  USD: '$',
  THB: '฿',
  EUR: '€',
};
const rightIcon = (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="18" height="18" rx="9" fill="#38C13D" />
    <path d="M3.5 8.5L7.5 12.5L14 6" stroke="white" strokeWidth="2" />
  </svg>
);

const errIcon = (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="1" y="1" width="18" height="18" rx="9" fill="white" stroke="#999999" strokeWidth="2" />
    <path
      d="M7 7L10 10M13 13L10 10M10 10L13 7L7 13"
      stroke="#999999"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

interface IData {
  desc: string;
  title: string;
}

const pricingPlans = [
  {
    name: 'Free',
    nameColor: '#38C13D',
    price: '0.00',
    price_year: '0.00',
    btnText: 'Free',
    btnBg: '#ffffff',
    btnTextColor: '#999999',
    credits: '100',
    estimated: '100',
  },
  {
    name: 'Basic',
    nameColor: '#0C0C0C',
    price: '49',
    price_year: '33.25',
    btnText: 'Subscribe',
    btnBg: '#725CFF',
    btnTextColor: '#FFFFFF',
    credits: '3,000',
    estimated: '1,500~3,000',
  },
  {
    name: 'Pro',
    nameColor: '#0C0C0C',
    price: '66.58',
    price_year: '99',
    btnText: 'Subscribe',
    btnBg: '#725CFF',
    btnTextColor: '#FFFFFF',
    credits: '10,000',
    estimated: '5,000~10,000',
  },
  {
    name: 'Elite',
    nameColor: '#0C0C0C',
    price: '99.92',
    price_year: '149',
    btnText: 'Subscribe',
    btnBg: '#725CFF',
    btnTextColor: '#FFFFFF',
    credits: '35,000',
    estimated: '17,500~35,000',
  },
];

export default function PricingContent({
  data,
  guideToRegisterOrLogin,
  isLogin,
  userCreditInfo,
  currentTab,
  aiDiscoverTaskId,
}: {
  data: IData;
  guideToRegisterOrLogin: () => void;
  isLogin: boolean;
  userCreditInfo: any;
  currentTab?: string;
  aiDiscoverTaskId?: number | null;
}) {
  const [currentItemId, setCurrentItemId] = useState(null);

  const { t } = useTranslation('common');
  const [isMonthly, setIsMonthly] = useState(true);
  const router = useRouter();
  const [subList, setSubList] = useState([]);
  const showPlan = useMemo(() => {
    if (isMonthly) {
      return subList.slice(0, 3);
    } else {
      return subList.slice(3, 8);
    }
  }, [isMonthly, subList]);

  useEffect(() => {
    if (userCreditInfo) {
      setIsMonthly(userCreditInfo?.cycle_type == 'monthly');
    }
    if (typeof window !== 'undefined' && navigator.language) {
      const browserLanguage = navigator.language;
      const prefix = browserLanguage.split('-')[0];
      orderSubsQueryRequest('email_finder', prefix ?? 'en').then((res) => {
        if (res.code === 0) {
          setSubList(res.data);
        }
      });
    }
  }, []);

  const showYearPrice = (index: number) => {
    if (index == 0) {
      return '0.00';
    } else {
      return showPlan[index - 1]?.amount;
    }
  };

  const showPrice = (index: number) => {
    if (index == 0) {
      return '0.00';
    } else {
      if (isMonthly) {
        return showPlan[index - 1]?.amount;
      } else {
        return (showPlan[index - 1]?.amount / 12).toFixed(2);
      }
    }
  };

  const [isLoading, setIsLoading] = useState(false);
  const isExpired = useMemo(() => {
    return new Date(userCreditInfo?.expire_time) < new Date();
  }, [userCreditInfo]);
  const handleSubscribe = (itemId: string) => {
    if (!isLogin) {
      guideToRegisterOrLogin();
      return;
    }
    // 如果当前用户订阅的货币与套餐的货币不一致，则不显示订阅按钮
    if (userCreditInfo?.currency) {
      if (userCreditInfo?.currency != showPlan[1]?.currency) {
        return;
      }
    }

    if (userCreditInfo?.item_id == itemId && !isExpired) {
      return;
    }
    if (isLoading) {
      return;
    }
    if (!isExpired) {
      const hasItem = showPlan.filter((p) => p.item_id == userCreditInfo?.item_id).length > 0;

      if (!hasItem && userCreditInfo?.item_id) {
        return;
      }
      if (userCreditInfo?.item_id >= itemId) {
        return;
      }
    }

    if (userCreditInfo?.item_id < itemId && userCreditInfo?.item_id && !isExpired) {
      setIsLoading(true);
      orderUpgradeRequest(userCreditInfo.subs_id, itemId, '', router.locale, {
        // isPremium: true,
        bizFuncName: 'email_finder',
      }).then((res: any) => {
        setIsLoading(false);
        if (res.code === 0) {
          window.open(res.data?.pay_link, '_self');
        }
      });
    } else {
      setIsLoading(true);

      // 构建 redirect URL，包含 AI discover 参数
      let redirectUrl = location.pathname;

      // 如果是 AI discover tab 且有任务 ID，添加到 redirect 参数
      if (currentTab === 'ai-discover' && aiDiscoverTaskId) {
        const params = new URLSearchParams();
        params.set('tab', 'ai-discover');
        params.set('task_id', aiDiscoverTaskId.toString());
        redirectUrl = `${location.pathname}?${params.toString()}`;
      }

      orderSubsRequest(
        {
          item_id: itemId,
          redirect: redirectUrl,
        },
        router.locale,
        { isPremium: true, bizFuncName: 'email_finder' }
      ).then((res: any) => {
        if (res.code === 0) {
          setIsLoading(false);
          window.open(res.data?.pay_link, '_self');
        }
      });
    }
  };

  // 文案按钮
  const getButtonText = (index: number) => {
    if (index == 0) {
      return 'Free';
    }

    if (userCreditInfo?.expire_time) {
      if (isExpired) {
        return 'Subscribe';
      }
    }
    if (showPlan[index - 1]?.item_id == userCreditInfo?.item_id) {
      return 'Current Subscription';
    }

    const hasItem = showPlan.filter((p) => p.item_id == userCreditInfo?.item_id).length > 0;

    if (showPlan[index - 1]?.item_id > userCreditInfo?.item_id && hasItem) {
      return 'Upgrade';
    }
    return 'Subscribe';
  };
  // 是否可以购买 index
  const canPurchase = (index: number) => {
    if (index == 0) {
      return false;
    }
    if (!isLogin || !userCreditInfo?.item_id) {
      return true;
    }

    // 如果订阅的item_id不再show里面 直接返回false
    if (userCreditInfo?.expire_time) {
      if (isExpired) {
        return true;
      } else {
        showPlan[index - 1]?.item_id > userCreditInfo?.item_id;
      }
    }
    if (!showPlan.some((p) => p.item_id == userCreditInfo?.item_id)) {
      return false;
    }
    return showPlan[index - 1]?.item_id > userCreditInfo?.item_id;
  };

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const dom = document.getElementById('pricing-content');

    if (!dom) {
      return;
    }

    if (window.matchMedia('(max-width: 768px)').matches) {
      const secondCard = dom.children.item(1) as HTMLElement;
      dom.scrollLeft = secondCard.offsetLeft - 16;
      return;
    }

    dom.scrollLeft = dom.scrollWidth - 980;
  }, []);

  return (
    <div className="text-center max-w-[1260px] mx-auto pt-[110px] px-4 max-md:pt-16" id="pricing">
      <h2 className="text-4xl font-semibold max-md:text-2xl">{data?.title}</h2>
      <p className="primary-text-60 mt-4">{data?.desc}</p>

      <div className="flex justify-center mt-12 w-fit mx-auto p-1 bg-[#E3E6F3] rounded-full">
        <div
          className={cn('h-9 px-10 rounded-full flex items-center cursor-pointer primary-text-60', {
            'bg-white text-[--primary-color] font-semibold': isMonthly,
          })}
          onClick={() => setIsMonthly(true)}
        >
          {t('Monthly')}
        </div>
        <div
          className={cn('h-9 px-10 rounded-full flex items-center cursor-pointer primary-text-60 relative', {
            'bg-white text-[--primary-color] font-semibold': !isMonthly,
          })}
          onClick={() => setIsMonthly(false)}
        >
          {t('Annually')}
          <div className="absolute text-sm text-white rounded-full h-7 lg:px-5 px-2 bg-[#FF4E4E] flex-center lg:-right-[54px] -right-[30px] -top-3.5 font-semibold">
            {t('Free 4mo')}
          </div>
        </div>
      </div>

      {/* 定价卡片网格 */}
      <div className="mt-9 flex gap-5 overflow-x-auto scrollbar-hide" id="pricing-content">
        {pricingPlans.map((plan, index) => (
          <div
            key={index}
            className={`bg-white border flex-shrink-0 w-[300px] lg:flex-1 border-[#D9D9D9] rounded-xl p-5 text-left ${userCreditInfo?.item_id == showPlan[index - 1]?.item_id ? 'border-[#725CFF]' : ''}`}
            onClick={() => {
              if (index > 0) {
                setCurrentItemId(showPlan[index - 1]?.item_id);
                handleSubscribe(showPlan[index - 1]?.item_id);
              }
            }}
          >
            <div className="text-xl font-bold text-center mb-4" style={{ color: plan.nameColor }}>
              {t(showPlan[index - 1]?.name) || t(plan.name)}
            </div>
            <div className="flex items-start justify-center mb-0">
              <span className="text-lg font-bold">{cashMap[showPlan[0]?.currency as keyof typeof cashMap] || '$'}</span>
              <span className="text-[40px] font-bold leading-tight">{showPrice(index) || '99'}</span>
              <span className="text-lg opacity-30">{t('/mo')}</span>
            </div>
            <div className="h-[20px]  text-center text-sm text-gray-400">
              {!isMonthly && index != 0 && (
                <span>
                  {cashMap[showPlan[0]?.currency as keyof typeof cashMap] || '$'}
                  {showYearPrice(index)} {t('/Yr')}
                </span>
              )}
            </div>

            <button
              className={`w-full mt-2 h-[40px] text-base rounded-md  mb-[42px] border border-[#D9D9D9] ${canPurchase(index) ? 'cursor-pointer bg-[#725CFF] text-white' : 'cursor-not-allowed text-gray-400 bg-[#F3F4F6]'}`}
            >
              {isLoading && currentItemId == showPlan[index - 1]?.item_id ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                </div>
              ) : (
                t(getButtonText(index))
              )}
            </button>

            <div className="bg-[#F7FAFE] rounded-md p-3 mb-8 border border-[#D9D9D9]">
              <div className="flex items-center justify-between">
                <div className="text-sm ">
                  {t('Credits')} {index != 0 && t('/mo')}
                </div>
                <div className={`text-lg font-semibold text-right ${index > 0 && 'text-[#725CFF]'}`}>
                  {plan.credits}
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div className="text-sm opacity-60">{t('Estimated Emails')}</div>
                <div className="text-sm opacity-60 text-right">{plan.estimated}</div>
              </div>
            </div>

            <div className="space-y-8 mb-10">
              {[
                t('Use without linking IG account'),
                t('All Scraping Features'),
                t('Export leads to CSV/Excel'),
                t('Duplicate Filtering Included'),
                t('AI Smart Lead Search'),
                t('Email Validation'),
              ].map((feature, idx) => (
                <div key={idx} className="flex items-start gap-3">
                  <div>{index > 0 || idx < 5 ? rightIcon : errIcon}</div>
                  <div>{feature}</div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
